import { normalizeDomain } from "app/pages/digital-marketing/utils/validation/url/domainNormalization";
import { validateDomain } from "app/pages/digital-marketing/utils/validation/url/domainValidation";

import type { ITradeMarkFormData } from "./form-types";

const MIN_TRADEMARK_NAME_LENGTH = 2;
const MAX_TRADEMARK_NAME_LENGTH = 64;

export const DATA_FIELDS = ["name", "variationsAndTypos", "domain", "whiteListedDomains"] as const;
// Error message mapping for each field and error type
export const ERROR_MESSAGES = {
    name: {
        required: "monitor_and_protect.shared.trademark_config_form.trademark_name_required_error",
        maxLength: "monitor_and_protect.shared.trademark_config_form.trademark_name_max_length_error",
        tooShort: "monitor_and_protect.shared.trademark_config_form.trademark_name_too_short_error",
        invalidChars: "monitor_and_protect.shared.trademark_config_form.trademark_name_invalid_chars_error",
    },
    domain: {
        invalid: "monitor_and_protect.shared.trademark_config_form.domain_error",
        required: "monitor_and_protect.shared.trademark_config_form.domain_required_error",
    },
    whiteListedDomains: {
        invalid: "monitor_and_protect.shared.trademark_config_form.domain_error",
    },
    variationsAndTypos: {
        required: "monitor_and_protect.shared.trademark_config_form.variations_required_error",
    },
} as const;

export function validateFormField<T extends keyof ITradeMarkFormData>(field: T, value: string): string | null {
    const trimmedValue = value.trim();

    if (field === "name") {
        if (!trimmedValue) {
            return ERROR_MESSAGES.name.required;
        }
        if (trimmedValue.length < MIN_TRADEMARK_NAME_LENGTH) {
            return ERROR_MESSAGES.name.tooShort;
        }
        if (trimmedValue.length > MAX_TRADEMARK_NAME_LENGTH) {
            return ERROR_MESSAGES.name.maxLength;
        }
        if (!/^[\p{L}\p{N}\p{M}\-\s&.()]+$/u.test(trimmedValue)) {
            return ERROR_MESSAGES.name.invalidChars;
        }
    } else if (field === "variationsAndTypos") {
        const variations = trimmedValue
            .split("\n")
            .map((variation) => variation.trim())
            .filter(Boolean);
        if (!trimmedValue || variations.length === 0) {
            return ERROR_MESSAGES.variationsAndTypos.required;
        }
    } else if (field === "domain") {
        if (!trimmedValue) {
            return ERROR_MESSAGES.domain.required;
        }
        if (!validateDomain(normalizeDomain(trimmedValue)).isValid) {
            return ERROR_MESSAGES.domain.invalid;
        }
    } else if (field === "whiteListedDomains") {
        if (trimmedValue) {
            const domains = trimmedValue
                .split("\n")
                .map((domain) => domain.trim())
                .filter(Boolean);
            const hasInvalidDomain = domains.some((domain) => !validateDomain(normalizeDomain(domain)).isValid);
            if (hasInvalidDomain) {
                return ERROR_MESSAGES.whiteListedDomains.invalid;
            }
        }
    }

    return null;
}

export const validateForm = <T extends ITradeMarkFormData>(
    formState: T | undefined,
    formError: { [K in keyof T]?: string | null },
) => {
    if (!formState) {
        return false;
    }

    const domainsIsSet = Boolean(formState.domain?.trim());
    const nameIsSet = Boolean(formState.name?.trim());
    const variationsAndTyposIsSet = Boolean(formState.variationsAndTypos?.trim());
    const formHasNoErrors = Object.values(formError).every((error) => !error);

    return domainsIsSet && nameIsSet && variationsAndTyposIsSet && formHasNoErrors;
};
